import { Injectable } from "@angular/core"
import { ApiService } from "./api.service"
import {
    Folder,
    GenerateSongParameters,
    GetContentResponse,
    LyricsMode,
    SongGenerationStreamEvent,
    Track,
    VocalGender,
} from "../../../../common-lib/general/interfaces/api/content.api"
import { BehaviorSubject } from "rxjs"
import { TracksHttpService } from "./tracks-http.service"
import { PlayerService } from "./audio/player.service"
import { UpdateTrackLoadingStatus } from "@common-lib/interfaces/api/sockets"
import { SongQueue, toSongQueueEntry } from "@common-lib/classes/songQueue"
import { cloneDeep } from "lodash"
import { GeneralService } from "./general/general.service"
import { FALLBACK_STREAMING_DURATION_SECONDS } from "@common-lib/constants/constants"

export type StartSongGenerationParameters = Omit<
    GenerateSongParameters,
    "parentID"
>

export interface SongGenerationAcknowledgement {
    accepted: boolean
    pendingCount: number
    error?: string
}

export interface SongGenerationClientHandle {
    acknowledgement: Promise<SongGenerationAcknowledgement>
    completion: Promise<void>
}

interface ActiveSongGeneration {
    requestID: string
    accountRevision: number
    parentID: string
    parameters: GenerateSongParameters
    acknowledged: boolean
    terminalHandled: boolean
    tracksByIndex: Map<number, Track>
    placeholdersByComposition: Map<string, Track>
    settleAcknowledgement: (result: SongGenerationAcknowledgement) => void
    cancel?: () => void
}

@Injectable()
export class TracksService {
    public scrollToIndex$: BehaviorSubject<number> = new BehaviorSubject(0)

    public content$: BehaviorSubject<GetContentResponse> = new BehaviorSubject(
        this.getDefaultContent()
    )

    public get path(): { folderName: string; folderID: string }[] {
        return this.content$.value.pathToCurrentFolder
    }

    private readonly timeout = 15000
    private readonly pendingTrackIDPrefix = "pending-song:"
    private readonly pendingTracksByFolder = new Map<string, Track[]>()
    private readonly streamedAudioByTrack = new Map<
        string,
        Map<number, ArrayBuffer>
    >()
    private readonly activeSongGenerations = new Map<
        string,
        ActiveSongGeneration
    >()
    private accountRevision = 0
    private nextSongGenerationRequestID = 0
    private inProgressPollInterval: ReturnType<typeof setInterval> | null = null
    private inProgressPollStartedAt: number | null = null
    private readonly inProgressPollIntervalMs = 5000
    private readonly inProgressPollTimeoutMs = 15 * 60 * 1000

    constructor(
        private httpService: TracksHttpService,
        private apiService: ApiService,
        private playerService: PlayerService,
        private generalService: GeneralService
    ) {
        this.listenForSongGeneration()
        this.apiService.user.subscribe(user => {
            void this.handleAccountChange(user)
        })
    }

    private async handleAccountChange(user: string | null): Promise<void> {
        const revision = ++this.accountRevision

        await this.resetAccountState()

        if (user === null || revision !== this.accountRevision) {
            return
        }

        try {
            const data = await this.httpService.getFolderContent("")

            if (revision !== this.accountRevision) {
                return
            }

            await this.setContentAndResetSongQueue(data, revision)
        } catch (error) {
            if (revision !== this.accountRevision) {
                return
            }

            this.apiService.handleError(
                error instanceof Error
                    ? error.message
                    : "Your tracks could not be loaded."
            )
        }
    }

    private async resetAccountState(): Promise<void> {
        this.stopInProgressPolling()

        for (const state of this.activeSongGenerations.values()) {
            state.terminalHandled = true
            if (!state.acknowledged) {
                state.settleAcknowledgement({
                    accepted: false,
                    pendingCount: 0,
                })
            }
            try {
                state.cancel?.()
            } catch {
                // Account cleanup must continue even if cancellation fails.
            }
        }

        const pendingTrackIDs = new Set(this.streamedAudioByTrack.keys())
        for (const tracks of this.pendingTracksByFolder.values()) {
            for (const track of tracks) {
                pendingTrackIDs.add(track._id)
            }
        }
        for (const trackID of pendingTrackIDs) {
            this.clearPendingTrackAudio(trackID)
        }

        this.activeSongGenerations.clear()
        this.pendingTracksByFolder.clear()
        this.streamedAudioByTrack.clear()
        this.content$.next(this.getDefaultContent())
        this.scrollToIndex$.next(0)

        await this.playerService.setSongQueue(new SongQueue(), false)
    }

    private listenForSongGeneration() {
        this.apiService.socket.value.on(
            "updateTrackLoadingStatus",
            async (data: UpdateTrackLoadingStatus) => {
                this.updateLoadingStatus(data.id, data.loadingStatus)

                await this.playerService.updateGeneratedSongCache(
                    data.id,
                    data.loadingStatus,
                    data.buffer,
                    data.bufferStart,
                    data.bufferEnd,
                    data.index
                )
            }
        )
    }

    private async setContentAndResetSongQueue(
        data: GetContentResponse,
        revision = this.accountRevision
    ) {
        if (revision !== this.accountRevision) {
            return
        }

        const folderID = this.getFolderIDFromPath(data.pathToCurrentFolder)
        const pendingTracks = this.pendingTracksByFolder.get(folderID) ?? []
        const pendingTrackIDs = new Set(pendingTracks.map(track => track._id))
        const mergedData: GetContentResponse = {
            ...data,
            tracks: data.tracks
                .filter(track => !pendingTrackIDs.has(track._id))
                .concat(pendingTracks),
        }

        this.content$.next(mergedData)

        const songQueue = new SongQueue(
            [],
            mergedData.tracks
                .filter(
                    track =>
                        track.loading > 5 &&
                        (!this.isPendingTrack(track) ||
                            this.playerService.songIsCached(track._id))
                )
                .map(track => toSongQueueEntry(track)),
            this.playerService.query.currentSong
        )

        await this.playerService.setSongQueue(
            songQueue,
            this.playerService.query.isPlaying
        )

        if (revision !== this.accountRevision) {
            return
        }

        this.syncInProgressPolling()
    }

    private hasInProgressTracks(tracks: Track[]): boolean {
        return tracks.some(track => track.loading < 100)
    }

    private shouldPollInProgressTracks(): boolean {
        return (
            this.activeSongGenerations.size === 0 &&
            this.hasInProgressTracks(this.content$.value.tracks)
        )
    }

    private syncInProgressPolling(): void {
        if (!this.shouldPollInProgressTracks()) {
            this.stopInProgressPolling()
            return
        }

        if (this.inProgressPollInterval !== null) {
            return
        }

        this.inProgressPollStartedAt = Date.now()
        this.inProgressPollInterval = setInterval(() => {
            void this.pollInProgressTracks()
        }, this.inProgressPollIntervalMs)
    }

    private stopInProgressPolling(): void {
        if (this.inProgressPollInterval !== null) {
            clearInterval(this.inProgressPollInterval)
            this.inProgressPollInterval = null
        }

        this.inProgressPollStartedAt = null
    }

    private async pollInProgressTracks(): Promise<void> {
        const revision = this.accountRevision

        if (
            this.inProgressPollStartedAt !== null &&
            Date.now() - this.inProgressPollStartedAt >
                this.inProgressPollTimeoutMs
        ) {
            this.stopInProgressPolling()
            return
        }

        if (!this.shouldPollInProgressTracks()) {
            this.stopInProgressPolling()
            return
        }

        try {
            const folderID = this.getCurrentFolderID()
            const data = await this.httpService.getFolderContent(folderID)
            await this.setContentAndResetSongQueue(data, revision)
        } catch {
            // Keep polling while generation may still be running server-side.
        }
    }

    private getFolderIDFromPath(
        path: { folderName: string; folderID: string }[]
    ): string {
        return path[path.length - 1]?.folderID ?? ""
    }

    public getCurrentFolderID(): string {
        return this.getFolderIDFromPath(this.content$.value.pathToCurrentFolder)
    }

    public isPendingTrack(track: Track | string): boolean {
        const trackID = typeof track === "string" ? track : track._id
        if (this.isPlaceholderTrack(trackID)) return true
        if (typeof track !== "string" && track.loading < 100) return true
        return [...this.pendingTracksByFolder.values()].some(tracks =>
            tracks.some(pendingTrack => pendingTrack._id === trackID)
        )
    }

    public isPlaceholderTrack(track: Track | string): boolean {
        const trackID = typeof track === "string" ? track : track._id
        return trackID.startsWith(this.pendingTrackIDPrefix)
    }

    private getTrackForComposition(
        state: ActiveSongGeneration,
        compositionID?: string
    ): Track | undefined {
        if (compositionID === undefined) {
            return undefined
        }

        return state.placeholdersByComposition.get(compositionID)
    }

    private decodeBase64AudioChunk(audioBase64: unknown): ArrayBuffer | null {
        if (typeof audioBase64 !== "string" || audioBase64.length === 0) {
            return null
        }

        const binaryString = atob(audioBase64)
        const bytes = new Uint8Array(binaryString.length)

        for (let index = 0; index < binaryString.length; index++) {
            bytes[index] = binaryString.charCodeAt(index)
        }

        return bytes.buffer
    }

    private getContiguousAudioBuffer(trackID: string): ArrayBuffer | null {
        const audioChunks = this.streamedAudioByTrack.get(trackID)
        if (audioChunks === undefined || !audioChunks.has(0)) {
            return null
        }

        const contiguousChunks: ArrayBuffer[] = []
        for (let sequence = 0; audioChunks.has(sequence); sequence++) {
            contiguousChunks.push(audioChunks.get(sequence)!)
        }

        const totalLength = contiguousChunks.reduce(
            (sum, chunk) => sum + chunk.byteLength,
            0
        )
        const merged = new Uint8Array(totalLength)
        let offset = 0

        for (const chunk of contiguousChunks) {
            merged.set(new Uint8Array(chunk), offset)
            offset += chunk.byteLength
        }

        return merged.buffer
    }

    private async updatePendingTrackAudio(
        state: ActiveSongGeneration,
        event: SongGenerationStreamEvent
    ): Promise<void> {
        const track = this.getTrackForComposition(state, event.composition_id)
        const decodedChunk = this.decodeBase64AudioChunk(event.audio_base64)
        if (track === undefined || decodedChunk === null) {
            return
        }

        const sequence = Number.isInteger(event.sequence) ? event.sequence! : 0
        const audioChunks =
            this.streamedAudioByTrack.get(track._id) ??
            new Map<number, ArrayBuffer>()

        audioChunks.set(sequence, decodedChunk)
        this.streamedAudioByTrack.set(track._id, audioChunks)

        const contiguousAudio = this.getContiguousAudioBuffer(track._id)
        if (contiguousAudio === null) {
            return
        }

        const estimatedDuration =
            track.duration > 0
                ? track.duration
                : FALLBACK_STREAMING_DURATION_SECONDS
        const estimatedPlayableEnd = estimatedDuration * (track.loading / 100)

        await this.playerService.updateGeneratedSongCache(
            track._id,
            track.loading,
            contiguousAudio,
            0,
            estimatedPlayableEnd,
            0
        )

        if (
            state.terminalHandled ||
            state.accountRevision !== this.accountRevision
        ) {
            this.clearPendingTrackAudio(track._id)
        }
    }

    private clearPendingTrackAudio(trackID: string): void {
        this.streamedAudioByTrack.delete(trackID)
        this.playerService.removeGeneratedSongFromCache(trackID)
    }

    private updateLoadingStatus(
        trackID: string,
        loading: number,
        duration?: number
    ) {
        for (const track of this.content$.value.tracks) {
            if (track._id === trackID) {
                track.loading = loading

                if (duration) {
                    track.duration = duration
                }

                this.setContentAndResetSongQueue(this.content$.value)

                break
            }
        }
    }

    private getDefaultContent(): GetContentResponse {
        return {
            folders: [],
            tracks: [],
            pathToCurrentFolder: [
                {
                    folderName: "My Tracks",
                    folderID: "",
                },
            ],
        }
    }

    private getParentFolderID(): string {
        const path = this.content$.getValue().pathToCurrentFolder

        if (path.length === 1) {
            return ""
        }

        return path[path.length - 2].folderID
    }

    private createPendingTrack(
        state: ActiveSongGeneration,
        composition: { composition_id: string; index: number }
    ): Track {
        return {
            _id: `${this.pendingTrackIDPrefix}${state.requestID}:${composition.composition_id}`,
            userID: "",
            duration: 0,
            name: `Generating Song ${composition.index + 1}`,
            index: composition.index,
            style: "",
            firstDownload: null,
            prompt: {
                text: state.parameters.prompt,
                artist: undefined,
                genre: undefined,
                mood: undefined,
                song_title: undefined,
            },
            createdAt: new Date(),
            parentID: state.parentID,
            deleted: false,
            liked: false,
            loading: 5,
            titlePending: true,
            audioFormat: "mp3",
            ...(typeof state.parameters.lyrics === "string"
                ? { lyrics: state.parameters.lyrics }
                : {}),
        }
    }

    private publishPendingTracks(folderID: string): void {
        if (this.getCurrentFolderID() !== folderID) {
            return
        }

        const content = this.content$.value
        const pendingTracks = this.pendingTracksByFolder.get(folderID) ?? []
        const pendingTrackIDs = new Set(pendingTracks.map(track => track._id))

        this.content$.next({
            ...content,
            tracks: content.tracks
                .filter(track => !pendingTrackIDs.has(track._id))
                .concat(pendingTracks),
        })
    }

    private publishPendingTracksFor(tracks: Array<Track | undefined>): void {
        const folderIDs = new Set(
            tracks
                .filter((track): track is Track => track !== undefined)
                .map(track => track.parentID)
        )

        for (const folderID of folderIDs) {
            this.publishPendingTracks(folderID)
        }
    }

    private removePendingTrackIDsFromFolders(ids: Set<string>): string[] {
        const affectedFolderIDs: string[] = []

        for (const [folderID, tracks] of [...this.pendingTracksByFolder]) {
            const remaining = tracks.filter(track => !ids.has(track._id))
            if (remaining.length === tracks.length) {
                continue
            }

            affectedFolderIDs.push(folderID)
            if (remaining.length === 0) {
                this.pendingTracksByFolder.delete(folderID)
            } else {
                this.pendingTracksByFolder.set(folderID, remaining)
            }
        }

        return affectedFolderIDs
    }

    private forgetPendingTracks(trackIDs: string[]): void {
        const ids = new Set(trackIDs)
        if (ids.size === 0) {
            return
        }

        this.removePendingTrackIDsFromFolders(ids)

        for (const state of this.activeSongGenerations.values()) {
            for (const [index, track] of [...state.tracksByIndex]) {
                if (ids.has(track._id)) {
                    state.tracksByIndex.delete(index)
                }
            }
            for (const [compositionID, track] of [
                ...state.placeholdersByComposition,
            ]) {
                if (ids.has(track._id)) {
                    state.placeholdersByComposition.delete(compositionID)
                    this.clearPendingTrackAudio(track._id)
                }
            }
        }

        for (const trackID of ids) {
            this.clearPendingTrackAudio(trackID)
        }
    }

    private reassignPendingTracks(
        trackIDs: string[],
        newParentID: string
    ): void {
        const ids = new Set(trackIDs)
        const moved: Track[] = []

        for (const [folderID, tracks] of [...this.pendingTracksByFolder]) {
            const remaining: Track[] = []
            for (const track of tracks) {
                if (ids.has(track._id)) {
                    track.parentID = newParentID
                    moved.push(track)
                } else {
                    remaining.push(track)
                }
            }

            if (remaining.length === 0) {
                this.pendingTracksByFolder.delete(folderID)
            } else {
                this.pendingTracksByFolder.set(folderID, remaining)
            }
        }

        if (moved.length === 0) {
            return
        }

        const destination = this.pendingTracksByFolder.get(newParentID) ?? []
        const destinationIDs = new Set(destination.map(track => track._id))
        this.pendingTracksByFolder.set(
            newParentID,
            destination.concat(
                moved.filter(track => !destinationIDs.has(track._id))
            )
        )
    }

    private addCreatedSongTracks(
        state: ActiveSongGeneration,
        event: SongGenerationStreamEvent
    ): number {
        const tracks = (event.tracks ?? []).filter(
            track =>
                typeof track?._id === "string" &&
                typeof track.index === "number" &&
                Number.isInteger(track.index)
        )
        const folderTracks =
            this.pendingTracksByFolder.get(state.parentID) ?? []
        const existingIDs = new Set(folderTracks.map(track => track._id))

        for (const track of tracks) {
            track.createdAt = new Date(track.createdAt)
            state.tracksByIndex.set(track.index!, track)
            if (!existingIDs.has(track._id)) {
                folderTracks.push(track)
                existingIDs.add(track._id)
            }
        }

        this.pendingTracksByFolder.set(state.parentID, folderTracks)
        this.publishPendingTracks(state.parentID)
        return state.tracksByIndex.size
    }

    private addAcceptedSongPlaceholders(
        state: ActiveSongGeneration,
        event: SongGenerationStreamEvent
    ): number {
        const compositions = [...(event.compositions ?? [])].sort(
            (left, right) => left.index - right.index
        )
        const folderTracks =
            this.pendingTracksByFolder.get(state.parentID) ?? []

        for (const composition of compositions) {
            if (
                typeof composition.composition_id !== "string" ||
                !Number.isInteger(composition.index)
            ) {
                continue
            }

            const track =
                state.tracksByIndex.get(composition.index) ??
                this.createPendingTrack(state, composition)
            state.placeholdersByComposition.set(
                composition.composition_id,
                track
            )
            if (!folderTracks.some(candidate => candidate._id === track._id)) {
                folderTracks.push(track)
            }
        }

        this.pendingTracksByFolder.set(state.parentID, folderTracks)
        this.publishPendingTracks(state.parentID)

        return state.placeholdersByComposition.size
    }

    private updatePendingTrackLoading(
        state: ActiveSongGeneration,
        loading: number,
        compositionID?: string,
        publish = true
    ): void {
        const tracks = compositionID
            ? [state.placeholdersByComposition.get(compositionID)]
            : [...state.placeholdersByComposition.values()]

        for (const track of tracks) {
            if (track !== undefined) {
                track.loading = Math.max(track.loading, Math.min(100, loading))
                this.playerService.updateCurrentSongPlayback(track._id, {
                    loading: track.loading,
                    duration: track.duration,
                })
            }
        }

        if (publish) {
            this.publishPendingTracksFor(tracks)
        }
    }

    private updatePendingTrackDuration(
        state: ActiveSongGeneration,
        durationSeconds: unknown,
        compositionID?: string
    ): void {
        if (
            compositionID === undefined ||
            typeof durationSeconds !== "number" ||
            !Number.isFinite(durationSeconds) ||
            durationSeconds <= 0
        ) {
            return
        }

        const track = state.placeholdersByComposition.get(compositionID)
        if (track !== undefined) {
            track.duration = durationSeconds
            this.playerService.updateCurrentSongPlayback(track._id, {
                duration: track.duration,
                loading: track.loading,
            })
            this.publishPendingTracks(track.parentID)
        }
    }

    private removePendingTracks(
        state: ActiveSongGeneration,
        compositionID?: string
    ): void {
        const tracksToRemove = compositionID
            ? [state.placeholdersByComposition.get(compositionID)]
            : [
                  ...new Map(
                      [
                          ...state.tracksByIndex.values(),
                          ...state.placeholdersByComposition.values(),
                      ].map(track => [track._id, track])
                  ).values(),
              ]
        const idsToRemove = new Set(
            tracksToRemove
                .filter((track): track is Track => track !== undefined)
                .map(track => track._id)
        )
        const affectedFolderIDs =
            this.removePendingTrackIDsFromFolders(idsToRemove)

        if (compositionID !== undefined) {
            const removedTrack =
                state.placeholdersByComposition.get(compositionID)
            state.placeholdersByComposition.delete(compositionID)
            if (removedTrack !== undefined) {
                state.tracksByIndex.delete(removedTrack.index!)
                this.clearPendingTrackAudio(removedTrack._id)
            }
        } else {
            for (const track of tracksToRemove) {
                if (track !== undefined) {
                    this.clearPendingTrackAudio(track._id)
                }
            }
            state.tracksByIndex.clear()
            state.placeholdersByComposition.clear()
        }

        for (const folderID of affectedFolderIDs) {
            this.publishPendingTracks(folderID)
        }
    }

    private releasePendingTracks(state: ActiveSongGeneration): void {
        const releasedIDs = new Set(
            [
                ...state.tracksByIndex.values(),
                ...state.placeholdersByComposition.values(),
            ].map(track => track._id)
        )
        this.removePendingTrackIDsFromFolders(releasedIDs)
        for (const track of releasedIDs) {
            this.streamedAudioByTrack.delete(track)
        }
        state.tracksByIndex.clear()
        state.placeholdersByComposition.clear()
    }

    private updateCreatedTrack(
        state: ActiveSongGeneration,
        event: SongGenerationStreamEvent
    ): void {
        if (typeof event.track_id !== "string" || !event.track) return
        const track = [...state.tracksByIndex.values()].find(
            candidate => candidate._id === event.track_id
        )
        if (track === undefined) return

        if (track.titlePending === false) {
            const {
                name: _name,
                prompt: _prompt,
                titlePending: _titlePending,
                ...rest
            } = event.track
            Object.assign(track, rest)
        } else {
            Object.assign(track, event.track)
        }
        this.publishPendingTracks(track.parentID)
    }

    private async finishSongGeneration(
        state: ActiveSongGeneration,
        event: SongGenerationStreamEvent
    ): Promise<void> {
        if (state.terminalHandled) {
            return
        }

        if (state.accountRevision !== this.accountRevision) {
            state.terminalHandled = true
            return
        }

        state.terminalHandled = true
        this.activeSongGenerations.delete(state.requestID)

        const wasAcknowledged = state.acknowledged

        if (!wasAcknowledged) {
            let error: string | undefined
            if (event.type === "generation.error") {
                error = event.error?.message ?? "Song generation failed."
            } else if (
                (event.completed ?? event.track_ids?.length ?? 0) === 0
            ) {
                error = "No songs were generated."
            }
            state.settleAcknowledgement({
                accepted: false,
                pendingCount: 0,
                error,
            })
        }

        if (event.type === "generation.error") {
            this.removePendingTracks(state)
            void this.generalService.getSongGenerationQuota()
            if (wasAcknowledged) {
                this.apiService.handleError(
                    event.error?.message ?? "Song generation failed."
                )
            }
            return
        }

        const completed = event.completed ?? event.track_ids?.length ?? 0
        const failed = event.failed ?? 0

        if (completed === 0) {
            this.removePendingTracks(state)
        } else {
            this.releasePendingTracks(state)
        }

        void this.generalService.getSongGenerationQuota()

        if (completed > 0 && this.getCurrentFolderID() === state.parentID) {
            const revision = this.accountRevision
            try {
                const data = await this.httpService.getFolderContent(
                    state.parentID
                )

                if (
                    revision === this.accountRevision &&
                    this.getCurrentFolderID() === state.parentID
                ) {
                    await this.setContentAndResetSongQueue(data, revision)
                    this.scrollToIndex$.next(this.getNumberOfElements())
                }
            } catch (error) {
                if (revision !== this.accountRevision) {
                    return
                }

                this.apiService.handleError(
                    error instanceof Error
                        ? error.message
                        : "Your generated tracks could not be refreshed."
                )
            }

            if (revision !== this.accountRevision) {
                return
            }
        }

        if (failed > 0 && completed > 0) {
            const noun = failed === 1 ? "song" : "songs"
            this.apiService.handleError(
                `${failed} ${noun} could not be generated. Successfully generated songs were saved.`
            )
        } else if (completed === 0 && wasAcknowledged) {
            this.apiService.handleError("No songs were generated.")
        }
    }

    private async handleSongGenerationEvent(
        state: ActiveSongGeneration,
        event: SongGenerationStreamEvent
    ): Promise<void> {
        if (state.terminalHandled) {
            return
        }

        if (state.accountRevision !== this.accountRevision) {
            state.terminalHandled = true
            return
        }

        if (event.type === "tracks.created" && !state.acknowledged) {
            const pendingCount = this.addCreatedSongTracks(state, event)
            if (pendingCount > 0) {
                state.acknowledged = true
                state.settleAcknowledgement({ accepted: true, pendingCount })
                void this.generalService.getSongGenerationQuota()
            }
            return
        }

        if (event.type === "generation.accepted") {
            const pendingCount = this.addAcceptedSongPlaceholders(state, event)

            if (!state.acknowledged && pendingCount === 0) {
                await this.finishSongGeneration(state, {
                    type: "generation.error",
                    error: {
                        code: "invalid_acknowledgement",
                        message: "Song generation could not be started.",
                    },
                })
                return
            }

            if (!state.acknowledged) {
                state.acknowledged = true
                state.settleAcknowledgement({ accepted: true, pendingCount })
            }
            return
        }

        switch (event.type) {
            case "track.updated":
                this.updateCreatedTrack(state, event)
                break
            case "generation.queued":
                this.updatePendingTrackLoading(state, 10)
                break
            case "generation.started":
                this.updatePendingTrackLoading(state, 20)
                break
            case "composition.started":
                this.updatePendingTrackLoading(state, 30, event.composition_id)
                break
            case "composition.decoding_started":
                this.updatePendingTrackLoading(state, 45, event.composition_id)
                break
            case "audio.start":
                this.updatePendingTrackLoading(state, 55, event.composition_id)
                break
            case "audio.chunk":
                if (event.is_final === true) {
                    this.updatePendingTrackDuration(
                        state,
                        event.duration_seconds,
                        event.composition_id
                    )
                }
                this.updatePendingTrackLoading(
                    state,
                    Math.min(90, 60 + ((event.sequence ?? 0) + 1) * 4),
                    event.composition_id,
                    false
                )
                await this.updatePendingTrackAudio(state, event)
                if (
                    state.terminalHandled ||
                    state.accountRevision !== this.accountRevision
                ) {
                    return
                }
                this.publishPendingTracksFor([
                    this.getTrackForComposition(state, event.composition_id),
                ])
                break
            case "audio.done":
                this.updatePendingTrackLoading(state, 92, event.composition_id)
                break
            case "composition.completed":
                this.updatePendingTrackDuration(
                    state,
                    event.duration_seconds,
                    event.composition_id
                )
                this.updatePendingTrackLoading(state, 100, event.composition_id)
                if (event.composition_id !== undefined) {
                    const completedTrack = this.getTrackForComposition(
                        state,
                        event.composition_id
                    )
                    if (completedTrack !== undefined) {
                        this.streamedAudioByTrack.delete(completedTrack._id)
                    }
                }
                break
            case "composition.failed":
            case "composition.cancelled":
                this.removePendingTracks(state, event.composition_id)
                break
            case "generation.completed":
            case "generation.error":
                await this.finishSongGeneration(state, event)
                break
        }
    }

    public startSongGeneration(
        parameters: StartSongGenerationParameters
    ): SongGenerationClientHandle {
        let settleAcknowledgement: (
            result: SongGenerationAcknowledgement
        ) => void
        const acknowledgement = new Promise<SongGenerationAcknowledgement>(
            resolve => {
                settleAcknowledgement = resolve
            }
        )

        if (
            parameters.prompt.trim().length === 0 ||
            !Number.isInteger(parameters.numCompositions) ||
            parameters.numCompositions < 1 ||
            parameters.numCompositions > 2
        ) {
            this.apiService.handleError(
                "Describe the song and select one or two tracks."
            )
            settleAcknowledgement!({ accepted: false, pendingCount: 0 })
            return { acknowledgement, completion: Promise.resolve() }
        }

        const requestID = String(++this.nextSongGenerationRequestID)
        const parentID = this.getCurrentFolderID()
        const requestParameters: GenerateSongParameters = {
            ...parameters,
            prompt: parameters.prompt.trim(),
            parentID,
        }
        const state: ActiveSongGeneration = {
            requestID,
            accountRevision: this.accountRevision,
            parentID,
            parameters: requestParameters,
            acknowledged: false,
            terminalHandled: false,
            tracksByIndex: new Map(),
            placeholdersByComposition: new Map(),
            settleAcknowledgement: settleAcknowledgement!,
        }
        let eventProcessing = Promise.resolve()
        this.activeSongGenerations.set(requestID, state)

        let request: ReturnType<TracksHttpService["generateSong"]>

        try {
            request = this.httpService.generateSong(
                requestParameters,
                event => {
                    eventProcessing = eventProcessing.then(() =>
                        this.handleSongGenerationEvent(state, event)
                    )
                }
            )
        } catch {
            const completion = this.finishSongGeneration(state, {
                type: "generation.error",
                error: {
                    code: "request_failed",
                    message: "Song generation could not be started.",
                },
            })

            return { acknowledgement, completion }
        }

        state.cancel = request.cancel

        const completion = request.completion
            .then(() => eventProcessing)
            .then(async () => {
                if (!state.terminalHandled) {
                    await this.finishSongGeneration(state, {
                        type: "generation.error",
                        error: {
                            code: "stream_ended",
                            message: "Song generation ended unexpectedly.",
                        },
                    })
                }
            })
            .catch(async error => {
                await this.finishSongGeneration(state, {
                    type: "generation.error",
                    error: {
                        code: "request_failed",
                        message:
                            error instanceof Error
                                ? error.message
                                : "Song generation failed.",
                    },
                })
            })

        return { acknowledgement, completion }
    }

    public async createTrack(
        prompt: string,
        audioInfluence: File | undefined,
        duration: number,
        lyrics?: string,
        lyricsMode?: LyricsMode,
        vocalGender?: VocalGender
    ): Promise<{ success: boolean; tracks: Track[] }> {
        try {
            const result = await this.httpService.generateTrack(
                this.getParentFolderID(),
                prompt,
                audioInfluence,
                duration,
                lyrics,
                lyricsMode,
                vocalGender
            )

            if (result.result === -1) {
                this.apiService.handleError(result.message)

                return {
                    success: false,
                    tracks: [],
                }
            }

            if (result.result === 0) {
                this.apiService.handleError(result.message)

                return {
                    success: false,
                    tracks: [],
                }
            }

            const tracks: Track[] = result["tracks"]

            const content = this.content$.value
            const currentTrackLength = content.tracks.length

            content.tracks = content.tracks.concat(tracks)

            await this.setContentAndResetSongQueue(content)

            return {
                success: true,
                tracks: tracks,
            }
        } catch (error) {
            this.apiService.handleError(error)

            return {
                success: false,
                tracks: [],
            }
        }
    }

    public async createFolder(): Promise<Folder | undefined> {
        const parentID = this.path[this.path.length - 1].folderID

        const result = (await this.httpService.createFolder(parentID)) as any

        if (result === 0) {
            return undefined
        }

        const content = this.content$.value
        content.folders.push(result.folder)

        await this.setContentAndResetSongQueue(content)

        return result.folder
    }

    public async openFolder(folderID: string): Promise<void> {
        const revision = this.accountRevision
        const data = await this.httpService.getFolderContent(folderID)

        await this.setContentAndResetSongQueue(data, revision)
    }

    public getTrackByID(trackID: string): Track | undefined {
        return this.content$.value.tracks.find(t => t._id === trackID)
    }

    public async downloadTrack(track: Track): Promise<void> {
        if (track.loading !== 100 || this.isPlaceholderTrack(track)) {
            return
        }

        const result = await this.httpService.getDownloadURL({
            trackID: track._id,
            type: "mp3",
        })

        await this.apiService.downloadFileFromUrl(result.link)
    }

    public async moveContent(
        selectedFolders: Folder[],
        selectedTracks: Track[],
        newParentID: string
    ): Promise<boolean> {
        if (selectedTracks.some(track => this.isPlaceholderTrack(track))) {
            return false
        }

        const folderIDs = selectedFolders.map(f => f._id)
        const trackIDs = selectedTracks.map(t => t._id)

        try {
            const response = await this.httpService.moveContent({
                folderIDsToMove: folderIDs,
                trackIDsToMove: trackIDs,
                newParentID,
            })

            if (response.result !== 1) {
                return false
            }

            this.reassignPendingTracks(trackIDs, newParentID)

            const content: GetContentResponse = {
                ...this.content$.value,
                folders: this.content$.value.folders.filter(
                    f => !folderIDs.includes(f._id)
                ),
                tracks: this.content$.value.tracks.filter(
                    t => !trackIDs.includes(t._id)
                ),
            }

            await this.setContentAndResetSongQueue(content)

            return true
        } catch (error) {
            const message =
                error instanceof Error
                    ? error.message
                    : "Could not move the selected items"

            this.apiService.handleError(message)

            return false
        }
    }

    public async deleteContent(
        folderIDs: string[],
        trackIDs: string[]
    ): Promise<void> {
        if (trackIDs.some(trackID => this.isPlaceholderTrack(trackID))) {
            return
        }

        await this.httpService.deleteContent({
            folderIDsToDelete: folderIDs,
            trackIDsToDelete: trackIDs,
        })

        this.forgetPendingTracks(trackIDs)

        const content = {
            ...this.content$.getValue(),
            folders: this.content$
                .getValue()
                .folders.filter(f => !folderIDs.includes(f._id)),

            tracks: this.content$
                .getValue()
                .tracks.filter(t => !trackIDs.includes(t._id)),
        }

        this.setContentAndResetSongQueue(content)
    }

    public async goBackFolder(): Promise<void> {
        const revision = this.accountRevision
        const pathToCurrentFolder = this.content$.getValue().pathToCurrentFolder

        if (pathToCurrentFolder.length <= 1) {
            return
        }

        const parentID =
            pathToCurrentFolder[pathToCurrentFolder.length - 2].folderID
        const data = await this.httpService.getFolderContent(parentID)

        await this.setContentAndResetSongQueue(data, revision)
    }

    public async rename(
        data: Track | Folder,
        type: "folder" | "track",
        newName: string
    ): Promise<void> {
        if (type === "track" && this.isPlaceholderTrack(data._id)) {
            return
        }

        await this.httpService.renameContent({
            id: data._id,
            newName,
            type,
        })

        data.name = newName
        if (type === "track") {
            ;(data as Track).titlePending = false
        }
    }

    public async like(track: Track, like: boolean): Promise<void> {
        if (this.isPlaceholderTrack(track)) {
            return
        }

        await this.httpService.likeTrack({
            trackID: track._id,
            like,
        })

        track.liked = like
    }

    public getNumberOfElements(): number {
        return (
            this.content$.value.folders.length +
            this.content$.value.tracks.length
        )
    }
}
